import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'

import styled from 'styled-components'

import {
  createPoint,
  normalizePointCoordinateForBackend,
  updatePoint,
} from '../../../../../api/adminBuildingFetches'
import { ProjectFloorSection } from '../../../../../api/types'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../contexts/notificationProvider'
import { PointControls } from '../../../../common/ViewSelector/components/PointControls'
import {
  IGroup,
  PointGroupSelector,
} from '../../../../common/ViewSelector/components/PointGroupSelector/PointGroupSelector'
import { ViewSelector } from '../../../../common/ViewSelector/ViewSelector'
import { FloorPlanView } from '../../../building_page/components/FloorPlanView'
import { useFetchProjectFloorSectionsQuery } from '../../hooks/adminBuildingQueries'

import { IMapPoint, ManagedMapPoint } from './ManagedMapPoint'
import { useManagePointsContext } from './ManagePointsContext'

const DRAG_THRESHOLD_PX = 3

const getClickCoordinates = (event: React.MouseEvent) => {
  return {
    x: event.nativeEvent.offsetX,
    y: event.nativeEvent.offsetY,
  }
}

const getMapCoordinatesFromClient = (clientX: number, clientY: number) => {
  const img = document.getElementById('floor-plan') as HTMLImageElement | null
  if (!img) return null

  const rect = img.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return null

  const x = ((clientX - rect.left) / rect.width) * img.offsetWidth
  const y = ((clientY - rect.top) / rect.height) * img.offsetHeight
  return { x, y }
}

interface PointDragState {
  pointId: number
  originalX: number
  originalY: number
  hasMoved: boolean
  draggable: boolean
}

export const ManagePoints = () => {
  const { addNotification } = useNotifications()

  const { updateFloor, state: buildingState } = useBuildingContext()

  const {
    canEditPoints,
    canCreateNewPoints,
    canSelectPoints,
    canDeletePointGroups,
    canCreatePointGroups,
    aimingPointId,
    setAimingPointId,
  } = useManagePointsContext()

  const [groupsExpanded, setGroupsExpanded] = useState<boolean>(true)
  const [pointControlsExpanded, setPointControlsExpanded] =
    useState<boolean>(true)
  const [dragState, setDragState] = useState<PointDragState | null>(null)
  const [aimMousePos, setAimMousePos] = useState<{
    x: number
    y: number
  } | null>(null)

  const dragStateRef = useRef<PointDragState | null>(null)
  const aimMouseDownPos = useRef<{ x: number; y: number } | null>(null)
  const floorPointsRef = useRef(buildingState.floorData.points)

  useEffect(() => {
    floorPointsRef.current = buildingState.floorData.points
  }, [buildingState.floorData.points])

  useEffect(() => {
    dragStateRef.current = dragState
  }, [dragState])

  const projectFloorSectionsOnSuccess = (data: ProjectFloorSection[]) => {
    updateFloor({
      sections: data,
    })
  }

  useFetchProjectFloorSectionsQuery(
    buildingState.projectId,
    buildingState.floorId,
    projectFloorSectionsOnSuccess,
  )

  useEffect(() => {
    updateFloor({
      selectedPoints: new Set(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: updateFloor is intentionally omitted - this should only run once on mount
  }, [])

  const handleEscapeKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && aimingPointId !== null) {
        setAimingPointId(null)
        setAimMousePos(null)
      }
    },
    [aimingPointId, setAimingPointId],
  )

  const nudgeSelectedPoints = useCallback(
    async (dx: number, dy: number) => {
      const selectedPointIds: number[] = Array.from(
        buildingState.floorData.selectedPoints,
      ).map(pointId => Number(pointId))
      if (selectedPointIds.length === 0) return

      const pointsById = new Map(
        buildingState.floorData.points.map((point: IMapPoint) => [
          point.point_id,
          point,
        ]),
      )
      const nextPoints: IMapPoint[] = buildingState.floorData.points.map(
        (point: IMapPoint) => {
          if (!selectedPointIds.includes(point.point_id)) return point
          const nextX = normalizePointCoordinateForBackend(
            parseFloat(point.x) + dx,
          )
          const nextY = normalizePointCoordinateForBackend(
            parseFloat(point.y) + dy,
          )
          return {
            ...point,
            x: nextX ?? point.x,
            y: nextY ?? point.y,
          }
        },
      )

      updateFloor({ points: nextPoints })

      const updates = selectedPointIds.map((pointId: number) => {
        const movedPoint = nextPoints.find(
          (point: IMapPoint) => point.point_id === pointId,
        )
        if (!movedPoint) return Promise.resolve()
        return updatePoint(
          buildingState.projectId,
          buildingState.floorId,
          pointId,
          {
            x: movedPoint.x,
            y: movedPoint.y,
          },
        )
      })

      const results = await Promise.allSettled(updates)
      const hasError = results.some(result => result.status === 'rejected')
      if (hasError) {
        const rollbackPoints = nextPoints.map((point: IMapPoint) => {
          const original = pointsById.get(point.point_id)
          return original ?? point
        })
        updateFloor({ points: rollbackPoints })
        addNotification('Error nudging selected points', 'error')
      }
    },
    [
      addNotification,
      buildingState.floorData.points,
      buildingState.floorData.selectedPoints,
      buildingState.floorId,
      buildingState.projectId,
      updateFloor,
    ],
  )

  const handleArrowNudge = useCallback(
    (e: KeyboardEvent) => {
      if (!canEditPoints || aimingPointId !== null) return
      if (buildingState.floorData.selectedPoints.size === 0) return

      const target = e.target as HTMLElement | null
      const targetTag = target?.tagName?.toLowerCase()
      const isTypingTarget =
        targetTag === 'input' ||
        targetTag === 'textarea' ||
        targetTag === 'select' ||
        !!target?.isContentEditable
      if (isTypingTarget) return

      const step = e.shiftKey ? 5 : 1
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        void nudgeSelectedPoints(-step, 0)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        void nudgeSelectedPoints(step, 0)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        void nudgeSelectedPoints(0, -step)
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        void nudgeSelectedPoints(0, step)
      }
    },
    [
      aimingPointId,
      buildingState.floorData.selectedPoints,
      canEditPoints,
      nudgeSelectedPoints,
    ],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [handleEscapeKey])

  useEffect(() => {
    document.addEventListener('keydown', handleArrowNudge)
    return () => document.removeEventListener('keydown', handleArrowNudge)
  }, [handleArrowNudge])

  const aimingPoint =
    aimingPointId !== null
      ? buildingState.floorData.points.find(
          (p: IMapPoint) => p.point_id === aimingPointId,
        )
      : null

  const handleAimMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    aimMouseDownPos.current = { x: e.clientX, y: e.clientY }
  }

  const handleAimMouseMove = (e: React.MouseEvent) => {
    setAimMousePos({
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY,
    })
  }

  const handleAimClick = async (e: React.MouseEvent) => {
    if (!aimMouseDownPos.current) return

    const dx = e.clientX - aimMouseDownPos.current.x
    const dy = e.clientY - aimMouseDownPos.current.y
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) return

    if (!aimingPoint) return

    const clickX = e.nativeEvent.offsetX
    const clickY = e.nativeEvent.offsetY
    const pointX = parseFloat(aimingPoint.x)
    const pointY = parseFloat(aimingPoint.y)

    const angleDeg =
      (Math.atan2(clickY - pointY, clickX - pointX) * 180) / Math.PI + 90
    const yaw = Math.round(angleDeg * 1000) / 1000

    try {
      await updatePoint(
        buildingState.projectId,
        buildingState.floorId,
        aimingPoint.point_id,
        { yaw },
      )

      const updatedPoints = buildingState.floorData.points.map(
        (p: IMapPoint) => (p.point_id === aimingPointId ? { ...p, yaw } : p),
      )
      updateFloor({ points: updatedPoints })
      addNotification('Yaw updated successfully', 'success')
    } catch {
      addNotification('Error updating yaw', 'error')
    }

    setAimingPointId(null)
    setAimMousePos(null)
  }

  const onDoubleClickMap = async (event: React.MouseEvent) => {
    if (!canCreateNewPoints) {
      return
    }

    const { x, y } = getClickCoordinates(event)

    try {
      const newPoint = await createPoint(
        buildingState.projectId,
        buildingState.floorId,
        x,
        y,
      )

      updateFloor({
        points: [...buildingState.floorData.points, newPoint],
      })
    } catch (err) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.log('createPoint==>>', err)
    }
  }

  const updatePointState = useCallback(
    (pointId: number, x: number, y: number) => {
      const floorPoints = floorPointsRef.current
      const updatedPoints = floorPoints.map((point: IMapPoint) => {
        if (point.point_id !== pointId) {
          return point
        }
        return { ...point, x: String(x), y: String(y) }
      })

      floorPointsRef.current = updatedPoints
      updateFloor({ points: updatedPoints })
    },
    [updateFloor],
  )

  const togglePointSelection = useCallback(
    (point: IMapPoint) => {
      if (!canSelectPoints) {
        return
      }

      const clickedPointId = point.point_id
      const selectedPoints = new Set(buildingState.floorData.selectedPoints)
      const clickedPointSelected = selectedPoints.has(clickedPointId)
      const selectedGroups = new Set(buildingState.floorData.selectedGroups)

      if (clickedPointSelected) {
        selectedPoints.delete(clickedPointId)

        const pointGroupSet =
          buildingState.floorData.pointMappings.get(clickedPointId)

        if (pointGroupSet) {
          pointGroupSet.forEach((groupId: number) => {
            selectedGroups.delete(groupId)
          })
        }
      } else {
        selectedPoints.add(clickedPointId)
      }

      updateFloor({
        selectedPoints,
        selectedGroups,
      })
    },
    [
      buildingState.floorData.pointMappings,
      buildingState.floorData.selectedGroups,
      buildingState.floorData.selectedPoints,
      canSelectPoints,
      updateFloor,
    ],
  )

  const stopEventPropagation = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
  }

  const onUpdatePointPosition = useCallback(
    async (point: IMapPoint, originalX: number, originalY: number) => {
      try {
        await updatePoint(
          buildingState.projectId,
          buildingState.floorId,
          point.point_id,
          {
            x: point.x,
            y: point.y,
          },
        )

        addNotification('Point moved successfully', 'success')
      } catch {
        addNotification('Error moving point', 'error')
        updatePointState(point.point_id, originalX, originalY)
      }
    },
    [
      addNotification,
      buildingState.floorId,
      buildingState.projectId,
      updatePointState,
    ],
  )

  useEffect(() => {
    if (!dragState) return

    const onMove = (event: MouseEvent) => {
      event.preventDefault()

      const state = dragStateRef.current
      if (!state || !state.draggable) return

      const coords = getMapCoordinatesFromClient(event.clientX, event.clientY)
      if (!coords) return

      const dx = coords.x - state.originalX
      const dy = coords.y - state.originalY
      if (
        !state.hasMoved &&
        (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)
      ) {
        dragStateRef.current = { ...state, hasMoved: true }
      }

      updatePointState(state.pointId, coords.x, coords.y)
    }

    const onUp = () => {
      const state = dragStateRef.current
      if (!state) return

      setDragState(null)
      dragStateRef.current = null

      const point = floorPointsRef.current.find(
        (p: IMapPoint) => p.point_id === state.pointId,
      )
      if (!point) return

      if (!state.hasMoved) {
        togglePointSelection(point)
        return
      }

      void onUpdatePointPosition(point, state.originalX, state.originalY)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dragState, onUpdatePointPosition, togglePointSelection, updatePointState])

  const onMouseDownPoint = (
    e: React.MouseEvent<HTMLDivElement>,
    point: IMapPoint,
  ) => {
    if (aimingPointId !== null) {
      return
    }

    const draggable = canEditPoints && !point.has_image
    if (!draggable && !canSelectPoints) {
      return
    }

    stopEventPropagation(e)

    const originalX = parseFloat(point.x)
    const originalY = parseFloat(point.y)
    const nextDragState: PointDragState = {
      pointId: point.point_id,
      originalX,
      originalY,
      hasMoved: false,
      draggable,
    }

    dragStateRef.current = nextDragState
    setDragState(nextDragState)
  }

  const pointElements = buildingState.floorData.points.map(
    (point: IMapPoint) => (
      <ManagedMapPoint
        key={point.point_id}
        point={point}
        x={parseFloat(point.x)}
        y={parseFloat(point.y)}
        onMouseDown={onMouseDownPoint}
      />
    ),
  )

  const aimOverlay =
    aimingPointId !== null && aimingPoint ? (
      <AimOverlay
        key="aim-overlay"
        onMouseDown={handleAimMouseDown}
        onMouseMove={handleAimMouseMove}
        onClick={handleAimClick}
      >
        <AimBanner>
          Click on the map to set yaw direction &middot; Press Esc to cancel
        </AimBanner>
        {aimMousePos && (
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <line
              x1={parseFloat(aimingPoint.x)}
              y1={parseFloat(aimingPoint.y)}
              x2={aimMousePos.x}
              y2={aimMousePos.y}
              stroke="rgba(7, 60, 122, 0.6)"
              strokeWidth="2"
              strokeDasharray="6 4"
            />
          </svg>
        )}
      </AimOverlay>
    ) : null

  const mapChildren = aimOverlay
    ? [...pointElements, aimOverlay]
    : pointElements

  const zoomIconStyle: CSSProperties = {
    position: 'absolute',
    right: '0px',
    zIndex: 2,
  }
  const zoomInIconStyle: CSSProperties = { ...zoomIconStyle, bottom: '55px' }
  const zoomOutIconStyle: CSSProperties = { ...zoomIconStyle, bottom: '10px' }
  const selectAllPointsStyle: CSSProperties = {
    ...zoomIconStyle,
    bottom: '100px',
  }

  const onToggleGroupSelectorCheckbox = (
    group: IGroup,
    checked: boolean,
    groupPoints: number[],
  ) => {
    const selectedPoints = new Set(buildingState.floorData.selectedPoints)

    groupPoints.forEach(pointId => {
      checked ? selectedPoints.add(pointId) : selectedPoints.delete(pointId)
    })

    updateFloor({
      selectedPoints,
    })
  }

  return (
    <>
      <FloorPlanView
        floorData={buildingState.floorData}
        hideDateRangeSelector
        onDoubleClickMap={onDoubleClickMap}
        mapViewerChildren={mapChildren}
        zoomInIconStyle={zoomInIconStyle}
        zoomOutIconStyle={zoomOutIconStyle}
        selectAllPointsStyle={selectAllPointsStyle}
        showPoints={false}
        showSelectAllPoints={canSelectPoints}
      />
      <ViewSelectorContainer>
        <ViewSelector
          title="Groups"
          initialViewSelectorExpanded
          viewingExpanded={groupsExpanded}
          setViewingExpanded={setGroupsExpanded}
          maxHeight={175}
        >
          <PointGroupSelector
            parentOnToggleCheckbox={onToggleGroupSelectorCheckbox}
            hideDelete={!canDeletePointGroups}
            hideCreateNewGroup={!canCreatePointGroups}
          />
        </ViewSelector>

        {canSelectPoints && (
          <ViewSelector
            title="Point Controls"
            initialViewSelectorExpanded
            viewingExpanded={pointControlsExpanded}
            setViewingExpanded={setPointControlsExpanded}
            fitContent
          >
            <PointControls />
          </ViewSelector>
        )}
      </ViewSelectorContainer>
    </>
  )
}

const ViewSelectorContainer = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const AimOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
  z-index: 10;
  -webkit-user-select: none;
  user-select: none;
`

const AimBanner = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(7, 60, 122, 0.85);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 13px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 11;
`
